/**
 * /fishing-tours — public directory listing of charter operators
 * worldwide, powered by the same marine palette as Tackle.au.
 *
 * Each card is a real, currently-trading operator seeded into
 * hp_fishing_tours.  Unclaimed listings still take enquiries — the
 * operator gets a Resend email with the lead + a "claim your listing"
 * link.
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Anchor, Fish, MapPin, Search, Waves, Loader2,
         Star, Globe2 } from "lucide-react";
import TackleLogo from "../components/tackle/TackleLogo";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

export default function FishingToursPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tours, setTours] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(params.get("q") || "");
  const [debounced, setDebounced] = useState(query);
  const [region, setRegion] = useState(params.get("region") || null);
  const [country, setCountry] = useState(params.get("country") || null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 320);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    api.get(`/api/fishing-tours/regions`).then(r => setRegions(r.data || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (debounced && debounced.length >= 2) p.q = debounced;
    if (region)  p.region  = region;
    if (country) p.country = country;
    p.limit = 60;
    api.get(`/api/fishing-tours`, { params: p }).then(r => {
      setTours(r.data.results || []);
      setLoading(false);
    });
    // keep URL in sync
    const np = new URLSearchParams();
    if (debounced) np.set("q", debounced);
    if (region) np.set("region", region);
    if (country) np.set("country", country);
    setParams(np, { replace: true });
  }, [debounced, region, country]);

  const auRegions = regions.filter(r => r.country_code === "AU");
  const nzRegions = regions.filter(r => r.country_code === "NZ");
  const intlRegions = regions.filter(r => !["AU","NZ"].includes(r.country_code));

  const featured = useMemo(() => tours.filter(t => t.featured), [tours]);
  const rest     = useMemo(() => tours.filter(t => !t.featured), [tours]);

  return (
    <div className="tackle-shop" data-testid="fishing-tours-root">
      {/* Reuse Tackle.au top bar to feel like sister sites */}
      <header className="tackle-topbar">
        <a href="/tackle" className="tackle-topbar__brand">
          <TackleLogo />
          <span className="tackle-topbar__brandtext">
            Tackle<span className="tackle-topbar__dot">.</span>au
          </span>
        </a>
        <nav className="tackle-topbar__nav">
          <a href="/tackle">Shop</a>
          <a href="/fishing-tours" style={{ color: "#fbbf24" }}>Tours</a>
          <a href="/plaza/shop">Plaza</a>
        </nav>
        <a href="/sales" className="tackle-topbar__cart">List your charter</a>
      </header>

      {/* Hero */}
      <section className="tackle-hero">
        <div className="tackle-hero__waves" aria-hidden>
          <Waves size={140} className="tackle-hero__wave tackle-hero__wave--1" />
          <Waves size={120} className="tackle-hero__wave tackle-hero__wave--2" />
          <Waves size={160} className="tackle-hero__wave tackle-hero__wave--3" />
        </div>
        <div className="tackle-hero__copy">
          <span className="tackle-hero__eyebrow">
            <Anchor size={14} /> Charter directory · AU &amp; international
          </span>
          <h1 className="tackle-hero__title">
            Find a charter.<br/>
            <span className="tackle-hero__title-accent">Land a fish.</span>
          </h1>
          <p className="tackle-hero__sub">
            {tours.length || "Loading"} hand-curated fishing charter operators —
            Cairns black marlin, Darwin barra, NZ snapper, Florida sails,
            Norway cod, Maldives GTs, and everything in between. No booking
            fees. Enquire direct, the operator replies straight to your
            inbox.
          </p>
          <div className="tackle-hero__search">
            <Search size={18} />
            <input
              placeholder="Search by species, region, operator…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="fishing-tours-search"
            />
          </div>
        </div>
      </section>

      {/* Region pills */}
      <div className="tackle-pills" data-testid="fishing-tours-pills">
        <button
          className={`tackle-pill ${!region && !country ? "is-active" : ""}`}
          onClick={() => { setRegion(null); setCountry(null); }}
          data-testid="ftp-all"
        >
          <Fish size={14} /> All operators
        </button>
        <button
          className={`tackle-pill ${country === "AU" ? "is-active" : ""}`}
          onClick={() => { setCountry("AU"); setRegion(null); }}
          data-testid="ftp-au"
        >
          🇦🇺 Australia
          <span className="tackle-pill__count">
            {auRegions.reduce((s, r) => s + r.count, 0)}
          </span>
        </button>
        <button
          className={`tackle-pill ${country === "NZ" ? "is-active" : ""}`}
          onClick={() => { setCountry("NZ"); setRegion(null); }}
          data-testid="ftp-nz"
        >
          🇳🇿 New Zealand
          <span className="tackle-pill__count">
            {nzRegions.reduce((s, r) => s + r.count, 0)}
          </span>
        </button>
        <button
          className={`tackle-pill ${country && !["AU","NZ"].includes(country) ? "is-active" : ""}`}
          onClick={() => { setCountry("INT"); setRegion(null); }}
          data-testid="ftp-int"
        >
          <Globe2 size={14} /> International
          <span className="tackle-pill__count">
            {intlRegions.reduce((s, r) => s + r.count, 0)}
          </span>
        </button>
        {regions.slice(0, 6).map((r) => (
          <button
            key={r.region_slug}
            className={`tackle-pill ${region === r.region_slug ? "is-active" : ""}`}
            onClick={() => { setRegion(r.region_slug); setCountry(null); }}
            data-testid={`ftp-region-${r.region_slug}`}
          >
            <MapPin size={12} /> {r.region}
            <span className="tackle-pill__count">{r.count}</span>
          </button>
        ))}
      </div>

      <main className="tackle-main">
        {loading ? (
          <div className="tackle-loading">
            <Loader2 className="tackle-spin" size={28} /> Casting the line…
          </div>
        ) : tours.length === 0 ? (
          <div className="tackle-empty">
            <p>No charters match — try clearing filters.</p>
          </div>
        ) : (
          <>
            {featured.length > 0 && (
              <>
                <div className="tackle-main__header">
                  <h2 className="tackle-main__title">
                    <Star size={20} style={{ verticalAlign: -4, color: "#fbbf24" }} />
                    {" "}Featured charters
                  </h2>
                  <span className="tackle-main__count">{featured.length} hand-picked</span>
                </div>
                <div className="tackle-grid">
                  {featured.map(t => (
                    <TourCard key={t.slug} tour={t} onOpen={() => navigate(`/fishing-tours/${t.slug}`)} />
                  ))}
                </div>
              </>
            )}
            <div className="tackle-main__header" style={{ marginTop: 48 }}>
              <h2 className="tackle-main__title">
                {region
                  ? regions.find(r => r.region_slug === region)?.region
                  : country === "AU" ? "Australian charters"
                  : country === "NZ" ? "New Zealand charters"
                  : country === "INT" ? "International charters"
                  : "All charters"}
              </h2>
              <span className="tackle-main__count">
                {rest.length} {rest.length === 1 ? "operator" : "operators"}
              </span>
            </div>
            <div className="tackle-grid" data-testid="fishing-tours-grid">
              {rest.map(t => (
                <TourCard key={t.slug} tour={t} onOpen={() => navigate(`/fishing-tours/${t.slug}`)} />
              ))}
            </div>
          </>
        )}
      </main>

      <footer className="tackle-footer">
        <div className="tackle-footer__inner">
          <div>
            <TackleLogo />
            <p className="tackle-footer__strap">
              Hustle Directory · fishing charters · a free intro service.
              Operators here are real, currently-trading businesses.
              Listings marked <em>unclaimed</em> can be claimed in 60
              seconds by the verified owner.
            </p>
          </div>
          <div className="tackle-footer__cols">
            <div>
              <h4>Regions</h4>
              {regions.slice(0, 6).map(r => (
                <a key={r.region_slug} href="#"
                   onClick={(e) => { e.preventDefault();
                     setRegion(r.region_slug); setCountry(null); }}>
                  {r.region}
                </a>
              ))}
            </div>
            <div>
              <h4>For operators</h4>
              <a href="/sales">List your charter (free)</a>
              <a href="/restaurant-agreement">Cross-redemption network</a>
              <a href="/tackle">Tackle.au shop</a>
            </div>
          </div>
        </div>
        <div className="tackle-footer__legal">
          © {new Date().getFullYear()} Hustle Directory · charter listings ·
          a HustlePlaza service
        </div>
      </footer>
    </div>
  );
}

function TourCard({ tour, onOpen }) {
  const claimed = !!tour.claimed_at;
  return (
    <button
      onClick={onOpen}
      style={card.root}
      data-testid={`tour-card-${tour.slug}`}
    >
      <div style={{ ...card.media,
                    backgroundImage: `url("${tour.image || ''}")` }}>
        {claimed
          ? <span style={card.badgeClaimed}>✓ Claimed</span>
          : <span style={card.badgeUnclaimed}>Unclaimed</span>}
        {tour.featured && <span style={card.badgeFeatured}>★ Featured</span>}
      </div>
      <div style={card.body}>
        <div style={card.name}>{tour.name}</div>
        <div style={card.meta}>
          <MapPin size={11} /> {tour.region}, {tour.country}
        </div>
        <div style={card.species}>{tour.target_species}</div>
        {tour.price_from_aud && (
          <div style={card.price}>
            From <strong>${tour.price_from_aud.toLocaleString("en-AU")}</strong>
            {" "}<span style={card.priceSub}>· {tour.trip_length}</span>
          </div>
        )}
      </div>
    </button>
  );
}

const card = {
  root: { background: "white", border: "1px solid #e8e2d4", borderRadius: 12,
          overflow: "hidden", cursor: "pointer", padding: 0, textAlign: "left",
          font: "inherit", color: "inherit", display: "flex",
          flexDirection: "column", transition: "transform 0.2s, box-shadow 0.2s" },
  media: { aspectRatio: "4/3", backgroundSize: "cover", backgroundPosition: "center",
           position: "relative",
           background: "linear-gradient(135deg, #1aa3c7 0%, #051824 100%)",
           backgroundBlendMode: "overlay" },
  body: { padding: 16, display: "flex", flexDirection: "column", gap: 4,
          flex: 1 },
  name: { fontWeight: 700, fontSize: 15, lineHeight: 1.3,
          color: "#051824" },
  meta: { fontSize: 11, color: "#475569", display: "flex",
          alignItems: "center", gap: 4, marginTop: 2 },
  species: { fontSize: 12, color: "#1aa3c7", fontWeight: 600,
             marginTop: 4 },
  price: { fontSize: 13, color: "#0f172a", marginTop: 8 },
  priceSub: { color: "#64748b", fontWeight: 400 },
  badgeUnclaimed: { position: "absolute", top: 10, left: 10,
                    background: "rgba(251,191,36,0.95)", color: "#0f172a",
                    fontSize: 10, fontWeight: 700, padding: "3px 9px",
                    borderRadius: 999, letterSpacing: "0.04em",
                    textTransform: "uppercase" },
  badgeClaimed: { position: "absolute", top: 10, left: 10,
                  background: "rgba(34,197,94,0.95)", color: "white",
                  fontSize: 10, fontWeight: 700, padding: "3px 9px",
                  borderRadius: 999, letterSpacing: "0.04em",
                  textTransform: "uppercase" },
  badgeFeatured: { position: "absolute", top: 10, right: 10,
                   background: "rgba(15,23,42,0.92)", color: "#fbbf24",
                   fontSize: 10, fontWeight: 700, padding: "3px 9px",
                   borderRadius: 999, letterSpacing: "0.04em" },
};
